import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { warehousesApi } from '../../services/api';
import { Warehouse } from '../../types/warehouse';
import {
  Paper,
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Chip
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';

const WarehouseList: React.FC = () => {
  const navigate = useNavigate();
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [warehouseToDelete, setWarehouseToDelete] = useState<Warehouse | null>(null);

  const fetchWarehouses = async () => {
    try {
      setLoading(true);
      const data = await warehousesApi.getWarehouses();
      setWarehouses(data.warehouses);
      setLoading(false);
    } catch (err) {
      setError((err as Error).message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWarehouses();
  }, []);

  const handleAddWarehouse = () => {
    navigate('/warehouses/list/add');
  };

  const handleEditWarehouse = (id: string) => {
    navigate(`/warehouses/list/edit/${id}`);
  };

  const handleViewWarehouse = (id: string) => {
    navigate(`/warehouses/list/${id}`);
  };

  const handleDeleteClick = (warehouse: Warehouse) => {
    setWarehouseToDelete(warehouse);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (warehouseToDelete) {
      try {
        await warehousesApi.deleteWarehouse(warehouseToDelete._id);
        setDeleteDialogOpen(false);
        setWarehouseToDelete(null);
        // Refresh warehouses after deletion
        fetchWarehouses();
      } catch (err) {
        setError((err as Error).message);
        setDeleteDialogOpen(false);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setWarehouseToDelete(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3, maxWidth: 1200, mx: 'auto', mt: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2" dir="rtl">
          قائمة المستودعات
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddWarehouse}
        >
          إضافة مستودع
        </Button>
      </Box>

      {error && (
        <Typography color="error" mb={2}>
          {error}
        </Typography>
      )}

      {warehouses.length === 0 ? (
        <Typography variant="body1" align="center" mt={4} mb={4}>
          لا توجد مستودعات. قم بإضافة مستودع جديد.
        </Typography>
      ) : (
        <TableContainer component={Paper} elevation={0}>
          <Table sx={{ minWidth: 650 }} aria-label="warehouses table">
            <TableHead>
              <TableRow>
                <TableCell align="right">اسم المستودع</TableCell>
                <TableCell align="right">الموقع</TableCell>
                <TableCell align="right">المدير</TableCell>
                <TableCell align="right">الحالة</TableCell>
                <TableCell align="center">الإجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {warehouses.map((warehouse) => (
                <TableRow 
                  key={warehouse._id} 
                  hover
                  onClick={() => handleViewWarehouse(warehouse._id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell align="right">{warehouse.name}</TableCell>
                  <TableCell align="right">{warehouse.location || 'غير محدد'}</TableCell>
                  <TableCell align="right">{warehouse.manager || 'غير محدد'}</TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={warehouse.status === 'active' ? 'نشط' : 'غير نشط'} 
                      color={warehouse.status === 'active' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      aria-label="edit"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditWarehouse(warehouse._id);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      aria-label="delete"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(warehouse);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title" dir="rtl">
          {"تأكيد حذف المستودع"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description" dir="rtl">
            هل أنت متأكد من حذف المستودع "{warehouseToDelete?.name}"؟ لا يمكن التراجع عن هذا الإجراء.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            إلغاء
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            حذف
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default WarehouseList;